import { Tool } from '@modelcontextprotocol/sdk/types.js';
import path from 'path';
import { logger } from '../lib/logger.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { readText } from '../utils/fs.js';
import type { Config } from '../types/index.js';

/**
 * Permission generation tool for SmartStack RBAC system.
 *
 * Analyzes NavRoute controllers and outputs HasData() code to add to PermissionConfiguration.cs.
 * Does NOT generate migrations with raw SQL (forbidden by SmartStack conventions).
 *
 * Convention: {navRoute}.{action}
 * Example: "platform.administration.entra.read"
 *
 * Output: C# HasData() code to manually add to PermissionConfiguration.cs
 */

// Standard HTTP method to permission action mapping
const HTTP_METHOD_TO_ACTION: Record<string, string> = {
  'GET': 'read',
  'POST': 'create',
  'PUT': 'update',
  'PATCH': 'update',
  'DELETE': 'delete',
  'HEAD': 'read',
  'OPTIONS': 'public', // No permission required
};

// Standard CRUD actions for RBAC
const STANDARD_ACTIONS = ['read', 'create', 'update', 'delete'] as const;

interface Permission {
  code: string;
  name: string;
  description: string;
  category: string; // From NavRoute context (platform, business, extensions)
}

interface GeneratePermissionsOptions {
  navRoute?: string;
  actions?: string[];
  includeStandardActions?: boolean;
  includeWildcard?: boolean;
}

export const generatePermissionsTool: Tool = {
  name: 'generate_permissions',
  description: `Generate RBAC permissions for SmartStack controllers.

Analyzes NavRoute attributes and outputs HasData() C# code to add to PermissionConfiguration.cs.

IMPORTANT: This tool does NOT generate migrations with raw SQL (forbidden by SmartStack conventions).
Instead, it outputs HasData() code that must be manually added to the Configuration file.

Example:
  navRoute: "platform.administration.entra"
  Outputs HasData() code for:
    - platform.administration.entra.*
    - platform.administration.entra.read
    - platform.administration.entra.create
    - platform.administration.entra.update
    - platform.administration.entra.delete

After adding to PermissionConfiguration.cs, run: dotnet ef migrations add <MigrationName>`,

  inputSchema: {
    type: 'object',
    properties: {
      navRoute: {
        type: 'string',
        description: 'NavRoute path (e.g., "platform.administration.entra"). If not provided, scans all controllers.',
      },
      actions: {
        type: 'array',
        items: { type: 'string' },
        description: 'Custom actions to generate (default: read, create, update, delete)',
      },
      includeStandardActions: {
        type: 'boolean',
        default: true,
        description: 'Include standard CRUD actions (read, create, update, delete)',
      },
      includeWildcard: {
        type: 'boolean',
        default: true,
        description: 'Include wildcard permission (e.g., personal.myspace.tenants.*)',
      },
    },
  },
};

/**
 * Handler for the generate_permissions tool
 */
export async function handleGeneratePermissions(
  args: any,
  config: Config
): Promise<string> {
  try {
    const options: GeneratePermissionsOptions = {
      navRoute: args.navRoute,
      actions: args.actions || [],
      includeStandardActions: args.includeStandardActions !== false,
      includeWildcard: args.includeWildcard !== false,
    };

    const structure = await findSmartStackStructure(config.smartstack.projectPath);
    if (!structure) {
      throw new Error('SmartStack project structure not found');
    }

    let permissions: Permission[] = [];
    let report = '';

    if (options.navRoute) {
      // Generate permissions for a specific NavRoute
      permissions = generatePermissionsForNavRoute(
        options.navRoute,
        options.actions || [],
        options.includeStandardActions || false,
        options.includeWildcard || false
      );

      report = `## Permissions for NavRoute: ${options.navRoute}\n\n`;
      report += formatPermissionsReport(permissions);
    } else {
      // Scan all controllers and generate permissions
      const scannedPermissions = await scanControllersForPermissions(structure.api || structure.root);
      permissions = scannedPermissions;

      report = `## Permissions from All Controllers\n\n`;
      report += `Total controllers scanned: ${getUniqueNavRouteCount(permissions)}\n`;
      report += `Total permissions generated: ${permissions.length}\n\n`;
      report += formatPermissionsReport(permissions);
    }

    // Generate HasData() code (NOT raw SQL migrations)
    report += `\n\n## HasData() Code for PermissionConfiguration.cs\n\n`;
    report += `⚠️ **IMPORTANT**: Do NOT use migrationBuilder.Sql() - it's forbidden by SmartStack conventions.\n`;
    report += `Add this code to \`PermissionConfiguration.cs\` in the HasData() section, then create a migration.\n\n`;
    report += generateHasDataCode(permissions, options.navRoute || 'custom');

    report += `\n\n### Next Steps\n\n`;
    report += `1. Add the module ID variable in GetSeedData() method\n`;
    report += `2. Add the HasData entries to the return array\n`;
    report += `3. Run: \`dotnet ef migrations add <MigrationName> -o Persistence/Migrations\`\n`;
    report += `4. Run: \`dotnet ef database update\`\n`;

    return report;
  } catch (error: any) {
    logger.error('Error generating permissions:', error);
    throw error;
  }
}

/**
 * Generate permissions for a specific NavRoute
 */
function generatePermissionsForNavRoute(
  navRoute: string,
  customActions: string[],
  includeStandardActions: boolean,
  includeWildcard: boolean = true
): Permission[] {
  const permissions: Permission[] = [];
  const parts = navRoute.split('.');
  const context = parts[0]; // platform, business, extensions

  // Validate NavRoute format
  if (parts.length < 3) {
    throw new Error(`Invalid NavRoute format: ${navRoute}. Expected format: context.application.module`);
  }

  // Add wildcard permission first
  if (includeWildcard) {
    permissions.push({
      code: `${navRoute}.*`,
      name: formatPermissionName(navRoute, 'Full Access'),
      description: `Full ${parts[parts.length - 1]} management`,
      category: context,
    });
  }

  const actions = includeStandardActions
    ? [...STANDARD_ACTIONS, ...customActions]
    : customActions;

  for (const action of actions) {
    const code = `${navRoute}.${action}`;
    const name = formatPermissionName(navRoute, action);
    const description = formatPermissionDescription(navRoute, action);

    permissions.push({
      code,
      name,
      description,
      category: context,
    });
  }

  return permissions;
}

/**
 * Scan all controllers for NavRoute attributes and generate permissions
 */
async function scanControllersForPermissions(
  backendRoot: string
): Promise<Permission[]> {
  const permissions: Permission[] = [];

  // Support multiple project structures:
  // - SmartStack: src/SmartStack.Api/Controllers
  // - Legacy: Api/Controllers or Api.Core/Controllers
  const controllerDirs = [
    path.join(backendRoot, 'src', 'SmartStack.Api', 'Controllers'),
    path.join(backendRoot, 'SmartStack.Api', 'Controllers'),
    path.join(backendRoot, 'Api', 'Controllers'),
    path.join(backendRoot, 'Api.Core', 'Controllers'),
  ];

  for (const dir of controllerDirs) {
    try {
      const files = await readDirectoryRecursive(dir);

      for (const file of files) {
        if (file.endsWith('Controller.cs')) {
          const content = await readText(file);
          const navRoutes = extractNavRoutesFromController(content);

          for (const navRoute of navRoutes) {
            const httpMethods = extractHttpMethods(content);
            const actions = httpMethods
              .map(method => HTTP_METHOD_TO_ACTION[method])
              .filter(action => action && action !== 'public');

            const uniqueActions = [...new Set(actions)];
            const navRoutePermissions = generatePermissionsForNavRoute(
              navRoute,
              [],
              false
            );

            // Only generate permissions for detected actions
            permissions.push(
              ...navRoutePermissions.filter(p =>
                uniqueActions.includes(p.code.split('.').pop()!)
              )
            );
          }
        }
      }
    } catch (error) {
      // Directory might not exist, continue
      logger.debug(`Could not scan directory: ${dir}`);
    }
  }

  // Remove duplicates
  return Array.from(
    new Map(permissions.map(p => [p.code, p])).values()
  );
}

/**
 * Extract NavRoute attributes from controller content
 * Supports all NavRoute parameter formats:
 * - [NavRoute("path")]
 * - [NavRoute("path", Suffix = "suffix")]
 * - [NavRoute("path", CustomSegment = "segment")]
 * - [NavRoute("path", CustomSegment = "segment", Suffix = "suffix")]
 */
function extractNavRoutesFromController(content: string): string[] {
  const navRoutes: string[] = [];

  // Match NavRoute attribute with any parameters
  // Captures: group 1 = navRoute path, full match includes all parameters
  const regex = /\[NavRoute\s*\(\s*"([^"]+)"([^)]*)\)\]/g;

  let match;
  while ((match = regex.exec(content)) !== null) {
    const navRoute = match[1];
    const params = match[2] || '';

    // Extract Suffix parameter if present (Suffix appends to navRoute for permissions)
    const suffixMatch = params.match(/Suffix\s*=\s*"([^"]+)"/);
    const suffix = suffixMatch ? suffixMatch[1] : null;

    if (suffix) {
      // Suffix creates a sub-permission path (e.g., tenants + analytics = tenants.analytics)
      navRoutes.push(`${navRoute}.${suffix}`);
    } else {
      // CustomSegment only affects URL routing, not the permission path
      navRoutes.push(navRoute);
    }
  }

  return navRoutes;
}

/**
 * Extract HTTP methods from controller content
 */
function extractHttpMethods(content: string): string[] {
  const methods: string[] = [];
  const httpAttributes = ['HttpGet', 'HttpPost', 'HttpPut', 'HttpPatch', 'HttpDelete', 'HttpHead', 'HttpOptions'];

  for (const attr of httpAttributes) {
    if (content.includes(`[${attr}`)) {
      methods.push(attr.replace('Http', '').toUpperCase());
    }
  }

  return methods;
}

/**
 * Format a human-readable permission name
 */
function formatPermissionName(navRoute: string, action: string): string {
  const parts = navRoute.split('.');
  const module = parts[parts.length - 1];

  // Convert to Title Case
  const moduleName = module
    .split(/[-_]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const actionName = action.charAt(0).toUpperCase() + action.slice(1);

  return `${moduleName} - ${actionName}`;
}

/**
 * Format a permission description
 */
function formatPermissionDescription(navRoute: string, action: string): string {
  const parts = navRoute.split('.');
  const context = parts[0];
  const application = parts[1];
  const module = parts[2];

  const contextName = context.charAt(0).toUpperCase() + context.slice(1);
  const applicationName = application
    .split(/[-_]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  const moduleName = module
    .split(/[-_]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const actionVerb = getActionVerb(action);

  return `${actionVerb} ${moduleName} in ${contextName} > ${applicationName}`;
}

/**
 * Get action verb for description
 */
function getActionVerb(action: string): string {
  const verbs: Record<string, string> = {
    'read': 'View',
    'create': 'Create',
    'update': 'Update',
    'delete': 'Delete',
    'export': 'Export',
    'import': 'Import',
  };

  return verbs[action] || action.charAt(0).toUpperCase() + action.slice(1);
}

/**
 * Format permissions report
 */
function formatPermissionsReport(permissions: Permission[]): string {
  let report = '';

  // Group by category
  const byCategory = permissions.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {} as Record<string, Permission[]>);

  for (const [category, perms] of Object.entries(byCategory)) {
    report += `### ${category.charAt(0).toUpperCase() + category.slice(1)}\n\n`;
    report += `| Code | Name | Description |\n`;
    report += `|------|------|-------------|\n`;

    for (const p of perms) {
      report += `| \`${p.code}\` | ${p.name} | ${p.description} |\n`;
    }

    report += `\n`;
  }

  return report;
}

/**
 * Get unique NavRoute count
 */
function getUniqueNavRouteCount(permissions: Permission[]): number {
  const navRoutes = new Set(
    permissions.map(p => p.code.split('.').slice(0, -1).join('.'))
  );
  return navRoutes.size;
}

/**
 * Generate HasData() C# code for PermissionConfiguration.cs
 * This is the correct pattern - NOT raw SQL migrations
 */
function generateHasDataCode(permissions: Permission[], navRoute: string): string {
  const parts = navRoute.split('.');
  const moduleName = parts.length >= 3 ? parts[parts.length - 1] : 'custom';
  const moduleVarName = `${moduleName}ModuleId`;

  let code = '```csharp\n';
  code += `// 1. Add module ID variable (get from NavigationModuleSeedData.cs)\n`;
  code += `var ${moduleVarName} = Guid.Parse("XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"); // TODO: Replace with actual ID\n\n`;
  code += `// 2. Add these entries to the HasData() return array:\n`;

  for (const perm of permissions) {
    const isWildcard = perm.code.endsWith('.*');
    const action = perm.code.split('.').pop();

    // Generate a placeholder GUID (user must replace with real random GUID)
    const guidPlaceholder = generatePlaceholderGuid();

    if (isWildcard) {
      code += `new { Id = Guid.Parse("${guidPlaceholder}"), Path = "${perm.code}", Level = PermissionLevel.Module, IsWildcard = true, ModuleId = ${moduleVarName}, Description = "${perm.description}", CreatedAt = seedDate },\n`;
    } else {
      const actionEnum = getActionEnum(action || 'read');
      code += `new { Id = Guid.Parse("${guidPlaceholder}"), Path = "${perm.code}", Level = PermissionLevel.Module, Action = PermissionAction.${actionEnum}, IsWildcard = false, ModuleId = ${moduleVarName}, Description = "${perm.description}", CreatedAt = seedDate },\n`;
    }
  }

  code += '```\n';
  code += '\n⚠️ **IMPORTANT**: Replace all GUIDs with randomly generated ones using:\n';
  code += '```powershell\n';
  code += `1..${permissions.length} | ForEach-Object { [guid]::NewGuid().ToString() }\n`;
  code += '```\n';

  return code;
}

/**
 * Generate a placeholder GUID (NOT for production use - must be replaced)
 */
function generatePlaceholderGuid(): string {
  return 'XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX';
}

/**
 * Get PermissionAction enum value from action string
 */
function getActionEnum(action: string): string {
  const actionMap: Record<string, string> = {
    'read': 'Read',
    'create': 'Create',
    'update': 'Update',
    'delete': 'Delete',
    'assign': 'Assign',
    'execute': 'Execute',
    'export': 'Export',
    'import': 'Import',
  };
  return actionMap[action.toLowerCase()] || action.charAt(0).toUpperCase() + action.slice(1);
}

/**
 * Read directory recursively
 */
async function readDirectoryRecursive(dir: string): Promise<string[]> {
  const files: string[] = [];

  try {
    const entries = await import('fs/promises').then(fs => fs.readdir(dir, { withFileTypes: true }));

    for (const entry of entries) {
      const fullPath = path.join(dir, entry.name);

      if (entry.isDirectory()) {
        files.push(...await readDirectoryRecursive(fullPath));
      } else {
        files.push(fullPath);
      }
    }
  } catch {
    // Directory doesn't exist or not accessible
  }

  return files;
}
